import { BankStatementDetails } from '@/models/bank-statement/bank-statement-details';
import { BillingDetails } from '@/models/billing/BillingDetails';
import { Component, OnInit } from '@angular/core';
import { AppService } from '@services/app.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import * as XLSX from 'xlsx';

@Component({
  selector: 'app-transaction-details',
  templateUrl: './transaction-details.component.html',
  styleUrl: './transaction-details.component.scss'
})
export class TransactionDetailsComponent implements OnInit {

  billingList: BillingDetails[] = [];
  filteredList: BillingDetails[] = [];
  searchText: string = '';
  selectedStatus: string = '';
  startDate: number = 0;
  endDate: number = 0;
  totalReceivedAmount: number = 0;

  fromDate!: string;
  toDate!: string;
  todayMax!: string;

  bankStatementList: BankStatementDetails[] = [];
  filteredBankStatementList: BankStatementDetails[] = [];

  bankSearchText: string = '';
  bankVerificationStatus: string = '';

  bankFileName: string = '';

  verifiedCount: number = 0;
  notMatchedCount: number = 0;
  verifiedAmount: number = 0;
  

  constructor(
    private appService: AppService,
    private toastr: ToastrService,
    private spinner: NgxSpinnerService
  ) { }

  ngOnInit(): void {
    this.todayMax = this.formatDate(new Date());
    this.setLastWeekDates();
    this.getBillingDetailsByStartAndEndDate();
  }

  // Auto set last 7 days
  setLastWeekDates() {
    const today = new Date();
    const lastWeek = new Date();

    lastWeek.setDate(today.getDate() - 7);

    this.fromDate = this.formatDate(lastWeek);
    this.toDate = this.formatDate(today);

    this.convertToEpoch();
  }

  // format yyyy-MM-dd
  formatDate(date: Date): string {
    return date.toISOString().split('T')[0];
  }

  // convert to epoch 13 digit
  convertToEpoch() {
    this.startDate = new Date(this.fromDate).getTime();
    this.endDate = new Date(this.toDate).getTime();
  }

  // when user change date
  onDate() {

    if (!this.fromDate || !this.toDate) return;

    if (new Date(this.fromDate) > new Date(this.toDate)) {
      this.toastr.error('Start date should not be greater than End date');
      return;
    }

    this.convertToEpoch();
    this.getBillingDetailsByStartAndEndDate();
  }


  getBillingDetailsByStartAndEndDate() {
    this.spinner.show();
    this.appService.getBillingDetailsByStartAndEndDate(this.startDate, this.endDate)
      .subscribe({
        next: (res) => {
          this.billingList = res.sort((a, b) => b.billId - a.billId).filter(item => item.billingType !== 'SHOP') || [];
          this.filteredList = this.billingList;
          this.spinner.hide();
          this.applyLocalFilter();
        },
        error: (err) => {
          this.spinner.hide();
          this.toastr.error('Failed to load billing data');
          console.error(err);
        }
      });
  }

  getAllBillDetails() {
    this.spinner.show();
    this.appService.getAllBillDetails()
      .subscribe({
        next: (res) => {
          this.billingList = res.sort((a, b) => b.billId - a.billId) || [];
          this.filteredList = this.billingList;
          this.spinner.hide()
        },
        error: (err) => {
          this.spinner.hide();
          this.toastr.error('Failed to load billing data');
          console.error(err);
        }
      });
  }

  applyLocalFilter() {

    const search = this.searchText?.toLowerCase() || '';
    const status = this.selectedStatus;

    this.filteredList = this.billingList.filter(bill => {

      const matchSearch =
        !search ||
        bill?.customerName?.toLowerCase().includes(search) ||
        bill?.phoneNumber?.toLowerCase().includes(search) ||
        bill?.invoiceNumber?.toLowerCase().includes(search);

      const matchStatus =
        !status ||
        bill?.transactionDetails?.paymentStatus === status;

      return matchSearch && matchStatus;
    });
    this.calculateTotalReceived();
  }

  calculateTotalReceived() {

    this.totalReceivedAmount = this.filteredList
      .filter(bill =>
        bill?.transactionDetails?.paymentStatus === 'SUCCESS'
      )
      .reduce((sum, bill) =>
        sum + (bill?.transactionDetails?.amount || 0),
        0
      );

  }

  openBankFilePicker(): void {

  const fileInput =
    document.getElementById(
      'bankStatementFile'
    ) as HTMLInputElement;

  if (fileInput) {

    fileInput.click();

  }

}

  /**
 * Excel file upload
 */
onBankStatementFileSelected(event: Event): void {

  const input =
    event.target as HTMLInputElement;


  if (!input.files || input.files.length === 0) {
    return;
  }


  const file =
    input.files[0];


  const allowedExtensions =
    ['.xlsx', '.xls'];


  const extension =
    file.name
      .substring(
        file.name.lastIndexOf('.')
      )
      .toLowerCase();


  if (!allowedExtensions.includes(extension)) {

    this.toastr.error(
      'Please upload only Excel file (.xlsx or .xls)'
    );

    input.value = '';

    return;
  }


  this.bankFileName =
    file.name;


  const reader =
    new FileReader();


  reader.onload =
    (e: any) => {

      try {

        const data =
          new Uint8Array(
            e.target.result
          );


        const workbook =
          XLSX.read(
            data,
            {
              type: 'array'
            }
          );


        if (
          !workbook.SheetNames ||
          workbook.SheetNames.length === 0
        ) {

          this.toastr.error(
            'No Excel sheet found'
          );

          return;
        }


        const sheetName =
          workbook.SheetNames[0];


        const worksheet =
          workbook.Sheets[
            sheetName
          ];


        const excelData =
          XLSX.utils.sheet_to_json(
            worksheet,
            {
              defval: ''
            }
          );


        if (
          !excelData ||
          excelData.length === 0
        ) {

          this.toastr.error(
            'No data found in Excel'
          );

          return;
        }


        console.log(
          'Uploaded Bank Excel:',
          excelData
        );


        this.verifyBankStatement(
          excelData
        );

      }
      catch (error) {

        console.error(
          'Excel read error:',
          error
        );

        this.toastr.error(
          'Unable to read Excel file'
        );

      }

    };


  reader.readAsArrayBuffer(file);

}

getExcelValue(
  row: any,
  columnNames: string[]
): any {

  const keys =
    Object.keys(row);


  for (
    const columnName
    of columnNames
  ) {

    const foundKey =
      keys.find(
        key =>
          key
            .toString()
            .trim()
            .toLowerCase()
            .replace(/\s+/g, '') ===

          columnName
            .toString()
            .trim()
            .toLowerCase()
            .replace(/\s+/g, '')
      );


    if (
      foundKey !== undefined
    ) {

      return row[foundKey];

    }

  }


  return '';

}

parseAmount(value: any): number {

  if (
    value === null ||
    value === undefined ||
    value === ''
  ) {

    return 0;

  }


  if (
    typeof value === 'number'
  ) {

    return Number(
      value.toFixed(2)
    );

  }


  let amount =
    value
      .toString()
      .trim();


  amount =
    amount
      .replace(/₹/g, '')
      .replace(/,/g, '')
      .replace(/\s/g, '')
      .replace(/Cr/gi, '')
      .replace(/Dr/gi, '')
      .replace(/\./g, '.');


  const result =
    parseFloat(amount);


  return isNaN(result)
    ? 0
    : Number(result.toFixed(2));

}

verifyBankStatement(
  excelData: any[]
): void {

  this.spinner.show();


  try {

    const result:
      BankStatementDetails[] = [];


    excelData.forEach(
      (row: any) => {


        const bankDate =
          this.getExcelValue(
            row,
            ['Date']
          );


        const valueDate =
          this.getExcelValue(
            row,
            [
              'Value Date',
              'ValueDate'
            ]
          );


        const cheque =
          this.getExcelValue(
            row,
            [
              'CHQ',
              'Cheque',
              'CHQ No',
              'Cheque No'
            ]
          );


        const remarks =
          this.getExcelValue(
            row,
            [
              'Remarks',
              'Remark',
              'Description',
              'Narration'
            ]
          );


        const debit =
          this.parseAmount(
            this.getExcelValue(
              row,
              ['Debit']
            )
          );


        const credit =
          this.parseAmount(
            this.getExcelValue(
              row,
              ['Credit']
            )
          );


        const balance =
          this.getExcelValue(
            row,
            ['Balance']
          );


        const remarksText =
          remarks
            ?.toString()
            .trim()
            .toLowerCase() || '';


        let matchedBill:
          BillingDetails | null = null;


        /*
         * Only CREDIT transactions
         */
        if (
          credit > 0 &&
          remarksText
        ) {


          matchedBill =
            this.billingList.find(
              (
                bill: BillingDetails
              ) => {


                const transactionRef =
                  bill
                    ?.transactionDetails
                    ?.transactionRefId
                    ?.toString()
                    .trim()
                    .toLowerCase();


                const transactionAmount =
                  this.parseAmount(
                    bill
                      ?.transactionDetails
                      ?.amount
                  );


                if (
                  !transactionRef
                ) {

                  return false;

                }


                if (
                  transactionAmount <= 0
                ) {

                  return false;

                }


                /*
                 * CHECK 1:
                 * Reference exists inside Remarks
                 */
                const referenceMatched =
                  remarksText.includes(
                    transactionRef
                  );


                /*
                 * CHECK 2:
                 * Credit equals transaction amount
                 */
                const amountMatched =
                  Math.abs(
                    credit -
                    transactionAmount
                  ) < 0.01;


                /*
                 * BOTH MUST MATCH
                 */
                return (
                  referenceMatched &&
                  amountMatched
                );

              }
            ) || null;

        }


        const verified =
          matchedBill !== null;


        result.push({

          date:
            this.formatBankDate(
              bankDate
            ),

          remarks:
            remarks
              ?.toString() || '',

          credit:
            credit,

          verificationStatus:
            verified
              ? 'VERIFIED'
              : 'NOT MATCHED',


          matchedInvoiceNumber:
            matchedBill
              ?.invoiceNumber || '',


          matchedCustomerName:
            matchedBill
              ?.customerName || '',


          matchedPhoneNumber:
            matchedBill
              ?.phoneNumber || '',


          transactionRefId:
            matchedBill
              ?.transactionDetails
              ?.transactionRefId || '',


          transactionAmount:
            matchedBill
              ?.transactionDetails
              ?.amount || 0

        });

      }
    );


    this.bankStatementList =
      result;


    this.filteredBankStatementList =
      [
        ...result
      ];


    this.calculateBankVerificationSummary();


    this.spinner.hide();


    /*
     * Open modal
     */
    this.openBankStatementModal();


    this.toastr.success(
      `${this.verifiedCount} transaction(s) verified`
    );


    console.log(
      'Bank Verification:',
      result
    );

  }
  catch (error) {

    console.error(
      'Verification error:',
      error
    );


    this.spinner.hide();


    this.toastr.error(
      'Bank statement verification failed'
    );

  }

}

openBankStatementModal(): void {

  const modalElement =
    document.getElementById(
      'bankStatementModal'
    );


  if (!modalElement) {
    return;
  }


  const bootstrapModal =
    new (window as any).bootstrap.Modal(
      modalElement
    );


  bootstrapModal.show();

}

formatBankDate(value: any): string {

  if (
    value === null ||
    value === undefined ||
    value === ''
  ) {

    return '';

  }


  /*
   * Excel numeric date
   */
  if (
    typeof value === 'number'
  ) {

    const excelDate =
      XLSX.SSF.parse_date_code(
        value
      );


    if (excelDate) {

      const day =
        String(
          excelDate.d
        ).padStart(2, '0');


      const month =
        String(
          excelDate.m
        ).padStart(2, '0');


      const year =
        excelDate.y;


      return `${day}-${month}-${year}`;

    }

  }


  return value
    .toString()
    .trim();

}


calculateBankVerificationSummary(): void {

  this.verifiedCount =
    this.bankStatementList.filter(
      x => x.verificationStatus === 'VERIFIED'
    ).length;

  this.notMatchedCount =
    this.bankStatementList.filter(
      x => x.verificationStatus === 'NOT MATCHED'
    ).length;

  this.verifiedAmount =
    this.bankStatementList
      .filter(
        x =>
          x.verificationStatus === 'VERIFIED'
      )
      .reduce(
        (sum, x) =>
          sum + Number(x.transactionAmount || 0),
        0
      );
}

applyBankStatementFilter(): void {

  const search =
    this.bankSearchText
      ?.trim()
      .toLowerCase() || '';

  const status =
    this.bankVerificationStatus || '';

  this.filteredBankStatementList =
    this.bankStatementList.filter(
      (row: BankStatementDetails) => {

        const matchSearch =
          !search ||

          row.remarks
            ?.toLowerCase()
            .includes(search) ||

          row.transactionRefId
            ?.toLowerCase()
            .includes(search) ||

          row.matchedInvoiceNumber
            ?.toLowerCase()
            .includes(search) ||

          row.matchedCustomerName
            ?.toLowerCase()
            .includes(search) ||

          row.matchedPhoneNumber
            ?.toLowerCase()
            .includes(search);

        const matchStatus =
          !status ||
          row.verificationStatus === status;

        return matchSearch && matchStatus;
      }
    );
}

clearBankStatement(): void {

  this.bankStatementList = [];
  this.filteredBankStatementList = [];

  this.bankSearchText = '';
  this.bankVerificationStatus = '';

  this.bankFileName = '';

  this.verifiedCount = 0;
  this.notMatchedCount = 0;
  this.verifiedAmount = 0;

  const fileInput =
    document.getElementById(
      'bankStatementFile'
    ) as HTMLInputElement;

  if (fileInput) {
    fileInput.value = '';
  }
}

downloadBankVerificationExcel(): void {

  if (
    !this.filteredBankStatementList ||
    this.filteredBankStatementList.length === 0
  ) {
    this.toastr.warning(
      'No bank statement data available to download'
    );

    return;
  }

  const excelData =
    this.filteredBankStatementList.map(
      (row: BankStatementDetails, index: number) => ({

        'S.No': index + 1,

        'Date':
          row.date || '',

        'Remarks':
          row.remarks || '',

        'Credit':
          row.credit || 0,

        'Verification Status':
          row.verificationStatus,

        'Invoice Number':
          row.matchedInvoiceNumber || '',

        'Customer Name':
          row.matchedCustomerName || '',

        'Phone Number':
          row.matchedPhoneNumber || '',

        'Transaction Ref ID':
          row.transactionRefId || '',

        'Transaction Amount':
          row.transactionAmount || 0

      })
    );

  const worksheet =
    XLSX.utils.json_to_sheet(excelData);

  /*
   * Set column widths
   */
  worksheet['!cols'] = [

    { wch: 8 },
    { wch: 15 },
    { wch: 15 },
    { wch: 15 },
    { wch: 60 },
    { wch: 15 },
    { wch: 15 },
    { wch: 20 },
    { wch: 20 },
    { wch: 20 },
    { wch: 25 },
    { wch: 18 },
    { wch: 25 },
    { wch: 20 }

  ];

  const workbook =
    XLSX.utils.book_new();

  XLSX.utils.book_append_sheet(
    workbook,
    worksheet,
    'Bank Verification'
  );

  const fileName =
    `Bank_Transaction_Verification_${this.formatDate(new Date())}.xlsx`;

  XLSX.writeFile(
    workbook,
    fileName
  );

  this.toastr.success(
    'Bank verification Excel downloaded'
  );
}

  clearFilter() {
    this.searchText = '';
    this.selectedStatus = '';
    this.filteredList = [...this.billingList];
    this.totalReceivedAmount = 0;
  }
}